import { useLayoutEffect, useState } from 'react';

/**
 * How far the visible viewport stops short of an element's bottom edge — the
 * keyboard when it is up, Safari's bar when it is over the page — measured
 * live (the same reading as IntroCard's useFootCover). Used to lift a bottom
 * sheet or a Save band above whatever covers the page's foot.
 *
 * @param {React.RefObject<HTMLElement>} ref
 * @returns {number} px
 */
const useViewportOverlap = (ref) => {
  const [overlap, setOverlap] = useState(0);
  useLayoutEffect(() => {
    const vv = window.visualViewport;
    const el = ref.current;
    if (!vv || !el) return undefined;
    const measure = () => {
      const visibleBottom = vv.offsetTop + vv.height;
      const next = Math.max(0, Math.round(el.getBoundingClientRect().bottom - visibleBottom));
      setOverlap((o) => (o === next ? o : next));
    };
    measure();
    vv.addEventListener('resize', measure);
    vv.addEventListener('scroll', measure);
    // The PAGE scrolling moves the element without moving the visual viewport —
    // e.g. a Safari-tab screen holding its scroll at the bleed (useShellChrome
    // holdAt 64) runs AFTER this first measure (a parent's layout effect), and
    // the stale reading kept 64px of false "cover" under every sheet there
    // (Mark's v7986 screenshot; WebKit: padding 90 = 26 + 64 with the overlay
    // ending exactly at the visible bottom)
    window.addEventListener('scroll', measure);
    return () => {
      vv.removeEventListener('resize', measure);
      vv.removeEventListener('scroll', measure);
      window.removeEventListener('scroll', measure);
    };
  }, [ref]);
  return overlap;
};

export default useViewportOverlap;
